// @core/stores/calendar/CalendarTemplateStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { calendarApi } from '@core/api';
import type {
  CreateEventTemplateRequest,
  EventTemplate,
  UpdateEventTemplateRequest,
} from '@core/types/calendar';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class CalendarTemplateStore {
  // List state
  items: EventTemplate[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: EventTemplate | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, EventTemplate>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): EventTemplate | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: EventTemplate | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create event template
   */
  async CreateEventTemplate(data: CreateEventTemplateRequest): Promise<EventTemplate> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.CreateEventTemplate(data);
      const item = response.template;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete event template
   */
  async DeleteEventTemplate(template_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await calendarApi.DeleteEventTemplate(template_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== template_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === template_id) {
          this.currentItem = null;
        }

        this.removeFromCache(template_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get event template
   */
  async GetEventTemplate(template_id: string): Promise<EventTemplate> {
    // Check cache first
    const cached = this.cache.get(template_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await calendarApi.GetEventTemplate(template_id);
      const item = response.template;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List event templates
   */
  async ListEventTemplates(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await calendarApi.ListEventTemplates(params);

      runInAction(() => {
        this.items = response.templates?.templates ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update event template
   */
  async UpdateEventTemplate(template_id: string, data: UpdateEventTemplateRequest): Promise<EventTemplate> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await calendarApi.UpdateEventTemplate(template_id, data);
      const item = response.template;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const calendarTemplateStore = new CalendarTemplateStore();
